/**
 * The manager's movement view (product brief journey J4).
 *
 * "Sales director gets a 10%+ alert, or opens the team view weekly, sees per-rep
 * projection movement decomposed into system recalibration vs data change vs
 * named interventions, drills to the log entries with reasons — conversation
 * happens with context, not archaeology."
 *
 * The decomposition is the whole product here. A manager arriving from an alert
 * has one question — was that a person, and if so who and why — and the page is
 * built so the answer is on screen before any chart is read: the human share is
 * stated in words at the top of each rep, and the drill into the log is one
 * click from it. The bridge is there to show the *proportion*, not to be
 * decoded.
 *
 * Reps whose interventions alone moved their month past the threshold sort
 * first, because those are the conversations the week actually needs.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { ArrowRight, TrendingUp } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  MovementResponse,
  RepMovement,
} from "@shared-core/data/my-pipeline-types";
import {
  formatCurrencyGbpCompact,
  formatDayLongMonthOrDash,
} from "@shared-core/domain/formatters";
import {
  CAUSE_MEANING,
  humanShare,
  movementCloses,
} from "@shared-core/domain/forecast-movement";
import { MATERIAL_MOVE_PCT } from "@shared-core/domain/my-pipeline-interventions";
import { paths } from "@shared-core/routing/routes";
import { Badge } from "../app/components/primitives/badge";
import { Button } from "../app/components/primitives/button";
import { Card } from "../app/components/primitives/card";
import { WorkspaceNav } from "../app/components/domain/forecasting/WorkspaceNav";
import { MovementBridgeChart } from "../app/components/domain/forecasting/MovementBridgeChart";

export function MovementPage() {
  const adapter = useDataAdapter();
  const [movement, setMovement] = useState<MovementResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await adapter.getForecastMovement();
        if (!cancelled) setMovement(result);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Could not load movement",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adapter]);

  const reps = useMemo(() => {
    const all = movement?.reps ?? [];
    return [...all].sort((a, b) => {
      if (a.material !== b.material) return a.material ? -1 : 1;
      return Math.abs(b.intervention) - Math.abs(a.intervention);
    });
  }, [movement]);

  if (loading) {
    return (
      <PageFrame>
        <div className="h-56 animate-pulse rounded-lg border border-border bg-surface-subtle" />
      </PageFrame>
    );
  }

  if (error || !movement) {
    return (
      <PageFrame>
        <Card className="p-6">
          <h2 className="font-semibold">Could not load movement</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {error ?? "No data returned."}
          </p>
        </Card>
      </PageFrame>
    );
  }

  const delta = movement.closing - movement.opening;

  return (
    <PageFrame>
      <header>
        <h1 className="text-xl font-semibold">Forecast movement</h1>
        <p className="max-w-2xl text-sm text-muted-foreground">
          {movement.month_label} · since{" "}
          {formatDayLongMonthOrDash(movement.period_from)}. Every pound of
          movement is one of three things, so &ldquo;why did the forecast
          move&rdquo; is a question you answer by looking.
        </p>
      </header>

      <Card className="flex flex-wrap items-baseline gap-x-6 gap-y-2 p-4">
        <div>
          <div className="text-2xl font-semibold tabular-nums">
            {formatCurrencyGbpCompact(movement.closing)}
          </div>
          <div className="text-xs text-muted-foreground">
            team projected unbooked
          </div>
        </div>
        <div className="text-sm text-muted-foreground">
          from{" "}
          <span className="tabular-nums text-foreground">
            {formatCurrencyGbpCompact(movement.opening)}
          </span>{" "}
          ·{" "}
          <span className="tabular-nums text-foreground">
            {delta > 0 ? "+" : "−"}
            {formatCurrencyGbpCompact(Math.abs(delta))}
          </span>{" "}
          over the period
        </div>
        <Link
          to={paths.interventionLog()}
          className="ml-auto text-sm text-primary hover:underline"
        >
          Everyone&rsquo;s interventions
        </Link>
      </Card>

      <div className="space-y-4">
        {reps.map((rep) => (
          <RepCard key={rep.rep_id} rep={rep} />
        ))}
      </div>
    </PageFrame>
  );
}

function PageFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-4xl space-y-4 p-4 pb-20">
      <WorkspaceNav active="movement" />
      {children}
    </div>
  );
}

function RepCard({ rep }: { rep: RepMovement }) {
  const share = Math.round(humanShare(rep) * 100);
  // A decomposition that does not sum is worse than none, so say so rather than
  // drawing a bridge that quietly does not close.
  const closes = movementCloses(rep);

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-semibold">{rep.rep_name}</h2>
            {rep.material && (
              <Badge variant="warning">
                <TrendingUp className="size-3" />
                {Math.round(MATERIAL_MOVE_PCT * 100)}%+ from interventions
              </Badge>
            )}
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {formatCurrencyGbpCompact(rep.opening)} &rarr;{" "}
            <span className="font-medium text-foreground tabular-nums">
              {formatCurrencyGbpCompact(rep.closing)}
            </span>{" "}
            ({rep.delta > 0 ? "+" : "−"}
            {formatCurrencyGbpCompact(Math.abs(rep.delta))})
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Why the movement happened, and then the book it happened to —
              a manager reading a surprising row wants the second next. */}
          <Button variant="outline" size="sm" asChild>
            <Link to={`${paths.interventionLog()}?rep=${rep.rep_id}`}>
              {rep.intervention_count} interventions
              <ArrowRight className="size-3.5" />
            </Link>
          </Button>
          <Button variant="ghost" size="sm" asChild>
            <Link to={paths.repPipeline({ repId: rep.rep_id })}>
              Open book
              <ArrowRight className="size-3.5" />
            </Link>
          </Button>
        </div>
      </div>

      {/* The answer, in words, before any chart is read. */}
      <p className="mt-3 rounded-md bg-surface-subtle px-3 py-2 text-sm">
        {rep.intervention === 0 ? (
          <>
            Nobody touched {rep.rep_name.split(" ")[0]}&rsquo;s number this
            period — all of the movement is the system and the data.
          </>
        ) : (
          <>
            <strong className="tabular-nums">
              {rep.intervention > 0 ? "+" : "−"}
              {formatCurrencyGbpCompact(Math.abs(rep.intervention))}
            </strong>{" "}
            of this was people, across {rep.intervention_count}{" "}
            {rep.intervention_count === 1 ? "change" : "changes"} — about{" "}
            {share}% of everything that moved. {CAUSE_MEANING.intervention}
          </>
        )}
      </p>

      <MovementBridgeChart rep={rep} />

      {!closes && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          The three causes do not account for the whole movement here. That is a
          data problem, not a rounding one — worth raising.
        </p>
      )}
    </Card>
  );
}
